import React, { useCallback, useId, useRef, useState } from "react";
import { ImagePlus, Link2, Loader2, RefreshCw, Trash2, Upload, X } from "lucide-react";
import { AppButton, DropZone, FieldError, HelpText, IconButton, Input, Label, cn } from "../ui";
import { IMAGE_ACCEPT, formatBytes, uploadQuizImage } from "./quizMedia";

/**
 * The image on a question, an option or a passage: upload it (or drop it, or
 * paste a link), see it as students will, replace or remove it.
 *
 * Uploads go through quizMedia.uploadQuizImage, which resizes an oversized
 * photo to fit 1 MB rather than refusing it; the field says when it did.
 */

function useImageUpload(onChange) {
  const [state, setState] = useState({ busy: false, error: "", note: "" });

  const upload = useCallback(async (file) => {
    if (!file) return;
    setState({ busy: true, error: "", note: "" });
    try {
      const { url, size, resized } = await uploadQuizImage(file);
      onChange(url);
      setState({
        busy: false,
        error: "",
        note: resized ? `Resized to ${formatBytes(size)} to fit the 1 MB limit.` : "",
      });
    } catch (err) {
      setState({ busy: false, error: err.message, note: "" });
    }
  }, [onChange]);

  const clearMessages = useCallback(() => setState((s) => ({ ...s, error: "", note: "" })), []);
  return { ...state, upload, clearMessages };
}

/** The preview, as the exam screen will size it. Says so when a link is dead. */
function ImagePreview({ src, alt, className }) {
  const [broken, setBroken] = useState(false);
  const [lastSrc, setLastSrc] = useState(src);
  if (src !== lastSrc) {
    setLastSrc(src);
    setBroken(false);
  }
  if (broken) {
    return (
      <p className="tw-m-0 tw-rounded-sm tw-border tw-border-dashed tw-border-warning tw-bg-warning/[0.08] tw-px-3 tw-py-2 tw-text-[0.8125rem] tw-text-warning-fg">
        This image could not be loaded. Check the link, or upload the file instead.
      </p>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setBroken(true)}
      className={cn("tw-block tw-max-w-full tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface tw-object-contain", className)}
    />
  );
}

/**
 * The full field: label, preview or drop zone, an optional link, messages.
 *
 * Props: label, hint, value (url), onChange(url), allowLink (default true)
 */
export function QuizImageField({ label = "Image", hint, value, onChange, allowLink = true, className }) {
  const inputRef = useRef(null);
  const id = useId();
  const [drag, setDrag] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState("");
  const { busy, error, note, upload, clearMessages } = useImageUpload(onChange);

  const pick = () => inputRef.current?.click();
  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    upload(file);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (!busy) upload(e.dataTransfer.files?.[0]);
  };
  const onDrag = (e) => {
    e.preventDefault();
    if (busy) return;
    setDrag(e.type === "dragenter" || e.type === "dragover");
  };
  const applyLink = () => {
    const url = linkDraft.trim();
    if (!/^https?:\/\//i.test(url)) return;
    clearMessages();
    onChange(url);
    setLinkOpen(false);
    setLinkDraft("");
  };

  return (
    <div className={cn("tw-flex tw-flex-col tw-gap-1.5", className)}>
      <Label id={`${id}-label`}>{label}</Label>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        onChange={onFile}
        className="tw-hidden"
        tabIndex={-1}
        aria-hidden="true"
      />

      {value ? (
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-2">
          <ImagePreview src={value} alt={`${label} preview`} className="tw-max-h-56" />
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <AppButton type="button" variant="outline" size="sm" onClick={pick} disabled={busy}>
              {busy ? <Loader2 size={16} className="tw-animate-spin" aria-hidden="true" /> : <RefreshCw size={16} aria-hidden="true" />}
              {busy ? "Uploading…" : "Replace"}
            </AppButton>
            <AppButton
              type="button"
              variant="ghost-destructive"
              size="sm"
              onClick={() => { clearMessages(); onChange(""); }}
              disabled={busy}
            >
              <Trash2 size={16} aria-hidden="true" /> Remove
            </AppButton>
          </div>
        </div>
      ) : (
        <DropZone
          size="sm"
          state={drag ? "active" : "idle"}
          disabled={busy}
          aria-labelledby={`${id}-label`}
          aria-describedby={`${id}-help`}
          onClick={pick}
          onDragEnter={onDrag}
          onDragOver={onDrag}
          onDragLeave={onDrag}
          onDrop={onDrop}
        >
          {busy
            ? <Loader2 size={18} className="tw-animate-spin tw-text-content-muted" aria-hidden="true" />
            : <Upload size={18} className="tw-text-content-subtle group-hover:tw-text-primary" aria-hidden="true" />}
          <span className="tw-text-sm tw-font-medium tw-text-content">
            {busy ? "Uploading…" : "Upload an image, or drop it here"}
          </span>
        </DropZone>
      )}

      <HelpText id={`${id}-help`}>
        {hint ? `${hint} · ` : ""}PNG, JPG, WebP or GIF, up to 1 MB. Larger photos are resized for you.
      </HelpText>
      {note && <HelpText>{note}</HelpText>}
      {error && <FieldError>{error}</FieldError>}

      {allowLink && !value && (
        linkOpen ? (
          <div className="tw-flex tw-items-center tw-gap-2">
            <Input
              aria-label="Image link"
              value={linkDraft}
              onChange={(e) => setLinkDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(); } }}
              placeholder="https://…"
            />
            <AppButton type="button" variant="outline" size="sm" onClick={applyLink} disabled={!/^https?:\/\//i.test(linkDraft.trim())}>
              Use link
            </AppButton>
            <IconButton icon={X} label="Cancel link" variant="ghost" size="sm" onClick={() => setLinkOpen(false)} />
          </div>
        ) : (
          <div>
            <AppButton type="button" variant="ghost" size="xs" onClick={() => setLinkOpen(true)}>
              <Link2 size={14} aria-hidden="true" /> Use a link instead
            </AppButton>
          </div>
        )
      )}
    </div>
  );
}

/**
 * The image on one answer option, in the option's own row: a thumbnail with a
 * remove control, or an "add image" button. Errors show under the row.
 *
 * Props: letter, value, onChange(url), disabled
 */
export function OptionImageControl({ letter, value, onChange, disabled }) {
  const inputRef = useRef(null);
  const { busy, error, note, upload, clearMessages } = useImageUpload(onChange);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    upload(file);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        onChange={onFile}
        className="tw-hidden"
        tabIndex={-1}
        aria-hidden="true"
      />
      {value ? (
        <div className="tw-flex tw-items-start tw-gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || busy}
            aria-label={`Replace the image for option ${letter}`}
            className="tw-cursor-pointer tw-rounded-sm tw-border-0 tw-bg-transparent tw-p-0 tw-focus-ring"
          >
            <ImagePreview src={value} alt="" className="tw-h-20 tw-w-auto tw-max-w-[10rem]" />
          </button>
          <IconButton
            icon={X}
            label={`Remove the image for option ${letter}`}
            variant="ghost-destructive"
            size="xs"
            onClick={() => { clearMessages(); onChange(""); }}
            disabled={disabled || busy}
          />
        </div>
      ) : (
        <AppButton
          type="button"
          variant="ghost"
          size="xs"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || busy}
        >
          {busy
            ? <Loader2 size={14} className="tw-animate-spin" aria-hidden="true" />
            : <ImagePlus size={14} aria-hidden="true" />}
          {busy ? "Uploading…" : `Add image to option ${letter}`}
        </AppButton>
      )}
      {note && <HelpText>{note}</HelpText>}
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
}
